export type StepStatus = 'locked' | 'in-progress' | 'complete';

export interface ActivationStepState {
  step: 1 | 2 | 3;
  status: StepStatus;
}

export type CompanyStatusDocumentType = 'articles_of_incorporation' | 'tax_id_verification';

export interface CompanyStatusDocument {
  documentType: CompanyStatusDocumentType;
  documentUuid: string;
}

export interface BusinessVerificationInput {
  hasAgreedToTerms: boolean;
  hasSubmittedTin: boolean;
  documents: CompanyStatusDocument[];
  allDivisionsOnboardingComplete: boolean;
}

export const hasCompanyDocument = (documents: CompanyStatusDocument[], documentType: CompanyStatusDocumentType) =>
  documents.some((document) => document.documentType === documentType);

export type BankAccountStatus = 'active' | 'error' | 'pending_manual_verification';

export interface CompanyStatusBankAccount {
  uuid: string;
  status: BankAccountStatus;
}

export interface BankConnectionInput {
  bankAccount: CompanyStatusBankAccount | null;
}

export interface PaymentRoutingInput {
  totalCustomers: number;
  completedCustomers: number;
}

export interface DeriveActivationStepsInput {
  businessVerification: BusinessVerificationInput;
  bankConnection: BankConnectionInput;
  paymentRouting: PaymentRoutingInput;
}

export const isBusinessVerificationComplete = (input: BusinessVerificationInput): boolean =>
  input.hasAgreedToTerms &&
  input.hasSubmittedTin &&
  hasCompanyDocument(input.documents, 'tax_id_verification') &&
  hasCompanyDocument(input.documents, 'articles_of_incorporation');

export const isPaymentRoutingComplete = (input: PaymentRoutingInput): boolean =>
  input.totalCustomers > 0 && input.completedCustomers >= input.totalCustomers;

export const deriveActivationSteps = ({
  businessVerification,
  bankConnection,
  paymentRouting,
}: DeriveActivationStepsInput): ActivationStepState[] => {
  const step1Complete =
    isBusinessVerificationComplete(businessVerification) && businessVerification.allDivisionsOnboardingComplete;
  const step2Unlocked = businessVerification.hasAgreedToTerms && businessVerification.hasSubmittedTin;
  const step2Status: StepStatus = !step2Unlocked
    ? 'locked'
    : bankConnection.bankAccount?.status === 'active'
    ? 'complete'
    : 'in-progress';
  const step3Status: StepStatus =
    !step1Complete || step2Status !== 'complete'
      ? 'locked'
      : isPaymentRoutingComplete(paymentRouting)
      ? 'complete'
      : 'in-progress';

  return [
    { step: 1, status: step1Complete ? 'complete' : 'in-progress' },
    { step: 2, status: step2Status },
    { step: 3, status: step3Status },
  ];
};

export const countCompleteSteps = (steps: ActivationStepState[]): number =>
  steps.filter((step) => step.status === 'complete').length;

export const isActivationComplete = (steps: ActivationStepState[]): boolean =>
  steps.every((step) => step.status === 'complete');

export const hasAnyCompletedField = (
  businessVerification: BusinessVerificationInput,
  steps: ActivationStepState[]
): boolean =>
  businessVerification.hasAgreedToTerms ||
  businessVerification.hasSubmittedTin ||
  steps.some((step) => step.status === 'complete');
